import React, { useState } from 'react';
import '@glidejs/glide/dist/css/glide.core.css';
import imagesloaded from 'imagesloaded';

import { useImperativeHandle, useEffect, useRef, forwardRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import useCheckoutState from '@repo/checkout/hooks/useCheckoutState';
import { checkoutTypes } from '@repo/checkout/enums/checkout-types';

function Resize(Glide, Components, Events) {
  const AUTOHEIGHT = {
    mount() {
      this.calculateHeight();
      Events.on(['mount.after', 'update'], () => {
        this.calculateHeight();
      });

      // Add resize observer to handle dynamic size changes (e.g. padding)
      if (typeof ResizeObserver !== 'undefined') {
        this._lastWidth = 0;
        this.resizeObserver = new ResizeObserver(entries => {
          const entry = entries[0];
          if (entry) {
            const newWidth = entry.contentRect.width;
            if (newWidth !== this._lastWidth) {
              this._lastWidth = newWidth;
              // Full update so Glide recalculates slide widths
              Glide.update();
            } else {
              this.calculateHeight();
            }
          }
        });

        if (Components.Html.root) {
          this.resizeObserver.observe(Components.Html.root);
        }
      }

      // Fallback to window resize for older browsers
      this.onWindowResize = () => {
        Glide.update();
      };
      window.addEventListener('resize', this.onWindowResize);

      // Glide never calls unmount() on custom components; without this hook the
      // listeners outlive the instance and call update() on a destroyed Glide.
      Events.on('destroy', () => {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        window.removeEventListener('resize', this.onWindowResize);
      });
    },

    calculateHeight() {
      if (!Components.Html.slides || Components.Html.slides.length === 0) {
        return;
      }

      // Remove transition temporarily during calculation
      Components.Html.track.style.transition = 'none';

      imagesloaded(Components.Html.track, () => {
        this.set();
        // Restore transition after calculation
        requestAnimationFrame(() => {
          Components.Html.track.style.transition = 'height 0.2s ease-out';
        });
      });
    },

    set() {
      if (
        !Components.Html.slides ||
        Components.Html.slides.length === 0 ||
        Glide.index >= Components.Html.slides.length
      ) {
        return;
      }

      const currentSlide = Components.Html.slides[Glide.index];

      if (currentSlide && currentSlide.offsetHeight > 0) {
        const computedHeight = window.getComputedStyle(currentSlide).height;
        requestAnimationFrame(() => {
          Components.Html.track.style.height = computedHeight;
        });
      } else if (Components.Html.slides.length > 0) {
        const firstSlide = Components.Html.slides[0];
        if (firstSlide && firstSlide.offsetHeight > 0) {
          const computedHeight = window.getComputedStyle(firstSlide).height;
          requestAnimationFrame(() => {
            Components.Html.track.style.height = computedHeight;
          });
        }
      }
    },
  };

  Events.on(['run'], () => {
    AUTOHEIGHT.set();
  });

  return AUTOHEIGHT;
}

// eslint-disable-next-line react/display-name
const Carousel = forwardRef(({ options, children, count, clipStyle, aspectRatio }, ref) => {
  const sliderRef = useRef();
  const [sliderIndex, setSliderIndex] = useState(0);
  const glideInstanceRef = useRef(null);
  const [isClient, setIsClient] = useState(false);
  const { checkoutType } = useCheckoutState();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClient(true);
    }
  }, []);

  useImperativeHandle(ref, () => sliderRef.current);

  const forceHeightRecalculation = () => {
    if (glideInstanceRef.current) {
      const track = sliderRef.current?.querySelector('.glide__track');
      if (track) {
        track.style.transition = 'none';
        glideInstanceRef.current.update();
        requestAnimationFrame(() => {
          track.style.transition = 'height 0.2s ease-out';
        });
      }
    }
  };

  useEffect(() => {
    let cancelled = false;

    if (glideInstanceRef.current) {
      glideInstanceRef.current.destroy();
    }

    // Only galleries with more than one image need Glide, so it stays out of every
    // checkout's first load.
    import('@glidejs/glide').then(({ default: Glide }) => {
      if (cancelled || !sliderRef.current) return;

      const slider = new Glide(sliderRef.current, {
        rewind: false,
        gap: 0,
        ...options,
      });

      slider.on('run', () => setSliderIndex(slider.index));
      slider.mount({ Resize });
      glideInstanceRef.current = slider;
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        forceHeightRecalculation();
      }
    };

    const handleResize = () => {
      requestAnimationFrame(() => {
        forceHeightRecalculation();
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('resize', handleResize);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', handleResize);
      if (glideInstanceRef.current) {
        glideInstanceRef.current.destroy();
        glideInstanceRef.current = null;
      }
    };
  }, [isClient]);

  useEffect(() => {
    if (count > 0) {
      const track = sliderRef.current?.querySelector('.glide__track');
      if (track) {
        track.style.transition = 'none';
        forceHeightRecalculation();

        const timer = setTimeout(() => {
          requestAnimationFrame(() => {
            track.style.transition = '';
            forceHeightRecalculation();
          });
        }, 0);

        return () => clearTimeout(timer);
      }
    }
  }, [children, count]);

  return (
    <div className="glide relative" ref={sliderRef}>
      <div style={clipStyle ? { overflow: 'hidden', ...clipStyle } : undefined}>
        <div
          className="glide__track"
          data-glide-el="track"
          // Before Glide sets a pixel height the ratio holds the track at the first slide's
          // height, not the tallest; the width stops the ratio resizing the track once it does.
          style={{ transition: 'height 0.2s ease-out', aspectRatio, width: '100%' }}
        >
          <ul className="glide__slides">{children}</ul>
        </div>
      </div>

      <div className="glide__arrows" data-glide-el="controls">
        <button
          className={clsx(
            'transition-colors bg-white hover:bg-gray-50 px-1 rounded-full h-7 w-7 text-black absolute top-0 bottom-0 mt-auto mb-auto shadow focus:outline-none z-10',
            sliderIndex === 0 ? 'hidden' : '',
            {
              '-left-3.5': checkoutType !== checkoutTypes.EMBED,
              'left-1.5': checkoutType === checkoutTypes.EMBED,
            },
          )}
          data-glide-dir="<"
          name="previous"
        >
          <ChevronLeftIcon />
        </button>

        <button
          className={clsx(
            'transition-colors bg-white hover:bg-gray-50 px-1 rounded-full h-7 w-7 text-black absolute top-0 bottom-0 mt-auto mb-auto shadow focus:outline-none z-10',
            sliderIndex === count - 1 ? 'hidden' : '',
            {
              '-right-3.5': checkoutType !== checkoutTypes.EMBED,
              'right-1.5': checkoutType === checkoutTypes.EMBED,
            },
          )}
          data-glide-dir=">"
          name="next"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
});

// eslint-disable-next-line react/display-name
export const Slide = forwardRef(({ children }, ref) => {
  return (
    <li className="glide__slide" ref={ref}>
      {children}
    </li>
  );
});

export default {
  Carousel,
  Slide,
};
